/**
 * ブランド設定の型定義
 * OEM提供時にブランドごとのUI・機能・URL等を切り替えるための設定
 */

/** 初回アンケート (AttributeCollectionModal) の設問キー。
 *  backend services/survey_service.py の SURVEY_CONFIGS と揃えること。 */
export type SurveyQuestionKey = 'user_type' | 'gender' | 'occupation' | 'usage_purpose' | 'discovery_channel';

/** surveyQuestions 未指定ブランドの既定設問構成 (従来の 利用目的 → 性別) */
export const DEFAULT_SURVEY_QUESTIONS: SurveyQuestionKey[] = ['user_type', 'gender'];

export interface BrandConfig {
  /** ブランド識別子 */
  id: string;

  /** サービス名 */
  serviceName: string;

  /** 運営会社名 */
  companyName: string;

  /** 背景の明暗。ホーム下部セクション・フッターなど「背景に直接乗るテキスト/アイコン」の
   *  配色（ライト/ダーク）を切り替える基準。背景画像/グラデが明色なら 'light'、暗色なら 'dark'。
   *  未指定時は 'dark' 扱い (旧来の配色)。配色の実体は App.css の `[data-color-scheme]` で定義。 */
  colorScheme?: 'light' | 'dark';

  /** ロゴ画像パス（white版・暗色背景向け） */
  logoWhite: string;

  /** ロゴ画像パス（明色背景向け）。OrgAdmin のような明色テーマ画面で、
   *  logoWhite に CSS の invert フィルタをかけずに「元画像のまま」表示するための任意変種。
   *  未指定の場合は logoWhite に invert(1) を適用する従来挙動にフォールバック。 */
  logoDark?: string;

  /** favicon パス */
  favicon?: string;

  /** テーマカラー
   *
   *  3 色のロール:
   *  - **primary**: 共有フォルダタブ active / アコーディオン枠 / モーダル枠など、
   *    最も大きい主要面に使う色 (アンビエント寄り)。
   *  - **sub**: 個人フォルダタブ active / 生成ステータスバーなど、補助的な面に使う色。
   *  - **accent**: 主要 CTA ボタン (.btn-primary / .btn-modal-accent /
   *    .btn-generate-lg) / フォーカスリング / 選択リング / チェックボックスなど、
   *    主導線の強調色。
   */
  colors: {
    /** プライマリーカラー (大きい主要面用) */
    primary: string;
    /** primary のホバー派生 */
    primaryHover: string;
    /** primary の RGB 三要素 ("R G B" 形式)。`rgba(var(--brand-primary-rgb) / 0.2)` の
     *  ような半透明合成に使う。primary HEX と必ずしも一致させる必要はなく、
     *  半透明用のベース色として柔軟に指定して良い。 */
    primaryRgb: string;
    /** サブカラー (個人フォルダタブ等の補助面) */
    subSolid: string;
    /** sub のホバー派生 */
    subSolidHover: string;
    /** subSolid の RGB 三要素 ("R G B" 形式)。`rgba(var(--brand-sub-rgb) / 0.x)` の
     *  半透明合成に使う。 */
    subRgb: string;
    /** アクセントカラー (主要 CTA / 強調用)。
     *  `.btn-primary` グラデーション・`.btn-modal-accent` 背景・
     *  `.btn-generate-lg` 等の主要ボタンに使われる。 */
    accentSolid: string;
    /** accent のホバー派生 */
    accentSolidHover: string;
    /** accentSolid の RGB 三要素 ("R G B" 形式) */
    accentRgb: string;
    /** 背景グラデーション開始色 */
    bgGradientStart: string;
    /** 背景グラデーション中間色 (60% 地点)。未指定なら AppLayout.css の
     *  フォールバック #444444 (ダーク前提)。ライト基調のブランドは
     *  ここを明色にしないと画面中央に暗い帯が出る。 */
    bgGradientMid?: string;
    /** 背景グラデーション終了色 */
    bgGradientEnd: string;
  };

  /** 外部URL */
  urls: {
    /** サービスサイトURL */
    website: string;
    /** お問い合わせURL */
    contact: string;
    /** プライバシーポリシーURL */
    privacy: string;
    /** 利用規約URL（undefined時はアプリ内ページを使用） */
    terms?: string;
  };

  /** 機能フラグ */
  features: {
    /** ユーザー入力プロンプトの表示・編集 */
    promptVisibility: boolean;
    /** 静止画のバナーレイアウトエディタ (docs/features/banner-layout-template-plan.md)。
     *  未指定 / true で全ユーザーに公開。false は platform admin だけに出す */
    bannerLayoutEditor?: boolean;
    /** ヘルプセンター */
    helpCenter: boolean;
    /** ナビゲーションガイド（ステップガイド） */
    navigationGuide: boolean;
    /** お任せプラン（制作代行）導線 */
    omakasePlan: boolean;
    /** Amazon連携 */
    amazonIntegration: boolean;
    /** 商品登録導線でマニュアル(手動登録)を Amazon 連携より先に出すか。
     *  商品一覧のタブ並び順と、特急モードの商品選択タブの並び・初期選択に効く。
     *  amazonIntegration が true のブランドでのみ意味を持つ (未指定は Amazon が先頭)。 */
    productsManualFirst?: boolean;
    /** Google 広告連携 (素材プレビューの広告アップロード等) */
    googleIntegration: boolean;
    /** Canva 連携 (マイページの連携行・素材フッターの「Canvaで編集」・
     *  シナリオの「Canvaにアップロード」・マニュアル記事)。
     *  Canva アプリは全パートナー共有 (単一 CANVA_CLIENT_ID) で、Canva 側の
     *  「アプリに戻る」ボタンに共有アプリ名が表示されるため、他ブランドとの
     *  関係を見せたくないパートナーでは false にして導線ごと隠す。
     *  backend/routes/canva.py の CANVA_DISABLED_PARTNER_CODES と揃えること。 */
    canvaIntegration: boolean;
    /** SKU・JANコード欄の表示 */
    skuFields: boolean;
    /** 営業メール機能（admin） */
    salesEmail: boolean;
    /** Google Tag Manager */
    gtm: boolean;
    /** OEM向け属性管理画面（admin）。true でOEMブランド配下のみ表示 */
    oemAttributeManagement: boolean;
    /** LightMedia 運営者向けの /admin 画面。true の時のみフロントにルートが登録される。
     *  (バックエンドは @require_platform_admin で別途ガードされているが、ここではブランド単位で
     *  UI 自体を隠すためのフラグ) */
    platformAdmin: boolean;
    /** ログイン画面に新規登録リンクを表示するか。招待制など自由登録を受け付けないブランドでは false */
    signupEnabled: boolean;
    /** MainNav のタブ (スタジオ / 商品データ / AI俳優 等) とハンバーガーメニュー内の同項目を表示するか。
     *  テンプレート特化ブランド (usodeki) ではメニューを出さない (ルート自体は残る) */
    mainNavTabs: boolean;
    /** アプリ全体のヘッダー / フッター (モバイル下部タブ含む) を表示するか。
     *  usodeki はホーム + 素材パネルだけのモバイル特化構成のため false (クローム無し) */
    appChrome: boolean;
    /** ホーム (`/` 完全一致のみ) を未ログインでも閲覧可能にするか。
     *  true でも他のパスは従来通り AuthGate でログインを要求する。
     *  テンプレートは認証不要の /generation/attributes/public から取得するため、
     *  有効化には partners.public_home_enabled = true も必要 */
    publicHome: boolean;

    /** 組織の概念をユーザーに見せず、オンボーディングを自動で「すぐに使い始める」
     *  (個人ワークスペースの自動作成) として処理するか。true のブランドでは
     *  /onboarding の 2 択 UI を出さず、マウント時に skipOrganizationSetup を実行する。
     *  組織単位の注記 (アンケートの上限表記等) もこのフラグで隠す。 */
    autoPersonalWorkspace?: boolean;

    /** MainNav に「素材」タブを表示するか (hypeapp / usodeki)。未指定は false */
    materialsNavTab?: boolean;

    /** 利用状況 / マイページを別ルートへのフルリロード遷移でなく、ホーム上の
     *  オーバーレイ (UsodekiPageOverlay + pageOverlayStore) で開くか。
     *  1 画面構成 (SPA 的) ブランド用。メニューと決済完了画面の導線が対象。 */
    overlayPageNavigation?: boolean;

    /** ハンバーガーメニューを 利用状況 / マイページ / お問い合わせ / ログアウト の
     *  最小構成にするか (管理系・ミーティング項目も出さない)。 */
    minimalUserMenu?: boolean;

    /** 素材 UI を簡素化するか: 共有フォルダタブを出さず個人フォルダのみ・
     *  フィルタは取得元のみ・カードは画像のみ (メタ情報ペイン非表示)・
     *  プレビューの操作を基本操作に絞る・アップロードはファイル選択を先行させる。 */
    simpleMaterialsUi?: boolean;

    /** 利用状況ページをポイント残高のみに絞るか (使用量 / ストレージ統計を隠す) */
    dashboardPointsOnly?: boolean;

    /** マイページ (プロフィール) を簡素化するか: 組織概念に紐づく 所属組織 / 権限 と
     *  全身写真 (カメオ) の行を非表示にする。組織を見せない・カメオ導線を持たない
     *  ブランド (usodeki) 向け。 */
    simpleProfilePage?: boolean;

    /** ACP (AI Creator Program): テンプレにクリエイター名/ランクバッジを表示し、
     *  クリエイター公開プロフィールへの導線を出すか。usodeki で先行有効化。 */
    creatorProgram?: boolean;

    /** ナレーター選択 (Fish Audio) の「権利処理済み」フィルターを既定でオンにするか。
     *  オンのブランドではオフに切り替える際に注意喚起の確認ダイアログを出し、一括生成の
     *  自動選択も権利処理済みボイスの中から選ぶ。Fish Audio の公開ライブラリには実在の人物・
     *  番組の声を無断で再現した投稿ボイスが混在するため、パートナー提供先 (SAITE) では既定で隠す。
     *  未指定は false (LightMedia 自社ブランドは従来どおりオフ)。 */
    fishLicensedVoicesDefault?: boolean;

    /** 動画のプロンプト入力モードに 3 タブ目「モーショングラフィックス」(レイヤー素材を
     *  デザインを変えずに動かす生成方法) を出すか (docs/features/motion-graphics-mode-plan.md §5.1)。
     *  段階 3 の完了 (§10) で全ブランド true (一般公開)。表示はこのフラグ **かつ**
     *  `GET /api/motion-layered/availability` が enabled のとき (fail-closed。料金行・レンダラーの
     *  runtime・backend の暫定ゲートが揃うまでは true でも出ない)。SVG の受け入れも同じ条件。
     *  判定は features/motion/layeredPromptMode.ts の shouldShowMotionGraphicsTab。 */
    motionGraphicsPromptMode?: boolean;
  };

  /** UI表示ラベル（glossary統合） */
  labels: {
    tag: string;
    tags: string;
    tagManagement: string;
    brand: string;
    brands: string;
    brandManagement: string;
    aiActor: string;
    aiActors: string;
    product: string;
    products: string;
    material: string;
    materials: string;
    studio: string;
    /** studio の短い形 (モバイルのナビタブなど狭幅レイアウト用) */
    studioShort: string;
    organization: string;
    organizations: string;
    member: string;
    members: string;
    invitation: string;
    attributeMode: string;
    scenarioMode: string;
    /** scenarioMode から末尾「モード」を除いた短い形 (狭幅レイアウト用) */
    scenarioModeBase: string;
    /** シナリオ/絵コンテ の名詞形 (動作語を含まない) */
    scenarioNoun: string;
    scenarioSettings: string;
    scenarioGenerate: string;
    scenarioRegenerate: string;
    scenarioEdit: string;
    productName: string;
    productDescription: string;
    productNewDefault: string;
    /** `product` の短縮形（例: "商品"/"案件"）。一覧・登録などの見出しに用いる単語 */
    productShort: string;
  };

  /** 書き出し設定 */
  exportSettings: {
    /** 対応解像度 */
    resolutions: string[];
    /** 対応フォーマット */
    formats: {
      video: string[];
      image: string[];
    };
  };

  /** 対応広告プラットフォーム（空配列で全プラットフォーム表示） */
  platforms: string[];

  /** 初回アンケートの設問構成 (表示順)。backend の SURVEY_CONFIGS と一致させること。
   *  未指定は DEFAULT_SURVEY_QUESTIONS (利用目的 → 性別)。 */
  surveyQuestions?: SurveyQuestionKey[];

  /** ハンバーガーメニュー末尾 (お問い合わせの前) に出す外部リンク (姉妹サービス等) */
  menuExternalLinks?: Array<{ label: string; url: string }>;

  /** 認証関連の設定 */
  auth: {
    /** ログイン画面で ACCOUNT_NOT_FOUND (未登録) だった際の案内。
     *  「{messagePrefix}<a>{linkLabel}</a>{messageSuffix}」の形で文中にリンクを埋め込んで表示する。
     *  計画書: docs/requirements/login-auto-provision-gating-plan.md §4.6
     *  internal は appPath() 経由の SPA パス、external は別サイトの完全 URL。 */
    notFoundGuidance:
      | { type: 'internal'; path: string; messagePrefix: string; linkLabel: string; messageSuffix: string }
      | { type: 'external'; url: string; messagePrefix: string; linkLabel: string; messageSuffix: string };
    /** ログイン画面上部のパンくずリスト (サービスサイト階層への組み込み + 現在地表示)。
     *  未設定なら非表示。url なしの要素が現在地。SAITE (HypeApp 依頼2) 向け。
     *  ※ noindex 想定のアカウント開設ページには出さない (ログイン画面のみ)。 */
    loginBreadcrumb?: Array<{ label: string; url?: string }>;
    /** ログイン・アカウント開設ページのロゴをリンク化する遷移先 (サービスサイトトップ等)。
     *  未設定ならリンクなし (従来どおり)。SAITE (HypeApp 依頼3) 向け。 */
    logoHref?: string;
  };

  /** ホームページのバリアント（OEMごとに自由に切替可能） */
  homePageVariant: 'default' | 'hypeapp' | 'take8' | 'usodeki';

  /** ホームページ（ヒーローセクション）のコンテンツ */
  homePage: {
    /** プロンプト入力のプレースホルダー */
    promptPlaceholder: string;
  };
}
